import { fontWeight } from '@devrangex/rui'
import { SerializedStyles, css, keyframes } from '@emotion/react'
import { memo } from 'react'

import { Nation, UserRankWithAvg } from '../../api/rank'

import { NationFlag } from './nationalFlags'
import { useFetchPrizes } from './useFetchRank'


export type RankData = [
  UserRankWithAvg['rank'],
  UserRankWithAvg['rank'],
  UserRankWithAvg['nickName'],
  UserRankWithAvg['scoreWithAvg'],
  UserRankWithAvg['shopName'],
]

interface RankTableRowProps {
  tuple: RankData
  nation?: Nation
  cssProps?: SerializedStyles
}

const showUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const trStyle = css`
  opacity: 0;
  animation: ${showUp} 0.3s ease-in forwards;

  & td {
    &:nth-of-type(1) {
      min-width: 6vw;
      max-width: 8vw;
    }
    &:nth-of-type(2) {
      vertical-align: middle;
      min-width: 16vw;
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow-x: hidden;
    }
    &:nth-of-type(3) {
      min-width: 20vw;
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow-x: hidden;
    }
    &:nth-of-type(4) {
      min-width: 10vw;
      width: 100%;
    }
    &:nth-of-type(5) {
      min-width: 10vw;
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow-x: hidden;
      font-weight: 400;
      color: #696e76;
    }
  }

  &.first {
    position: relative;
    background-color: #21201a;

    & td {
      color: #f7d15f;

      &:first-of-type {
        background-image: linear-gradient(
          0deg,
          rgba(223, 192, 26, 1) 0%,
          rgba(255, 225, 65, 1) 100%
        );
        -webkit-background-clip: text;
        background-clip: text;
        color: transparent;
        font-size: 2.4vh;

        &::after {
          content: 'st';
          font-size: 1.8vh;
        }
      }
    }
  }

  &:nth-of-type(odd) {
    background-color: #1b1e22;
  }

  &.second {
    & td {
      &:first-of-type {
        background-image: linear-gradient(
          0deg,
          rgba(248, 248, 248, 0.86) 0%,
          #ffffff 100%
        );
        -webkit-background-clip: text;
        background-clip: text;
        color: transparent;
        font-size: 2vh;

        &::after {
          content: 'nd';
          font-size: 1.8vh;
        }
      }
    }
  }

  &.third {
    & td {
      &:first-of-type {
        background-image: linear-gradient(
          0deg,
          rgba(231, 163, 91, 0.77) 0%,
          #eca65c 100%
        );
        -webkit-background-clip: text;
        background-clip: text;
        color: transparent;
        font-size: 2vh;

        &::after {
          content: 'rd';
          font-size: 1.8vh;
        }
      }
    }
  }
`

const tdStyle = css`
  font-size: 1vw;
  font-weight: ${fontWeight.semibold};
  padding: 0 0.5vw;
  text-align: left;
  height: 6vh;

  &.number {
    font-family: 'inter';
    font-style: italic;
    text-align: center;
  }

  /* 닉네임 */
  &:nth-of-type(3) {
    display: inline-flex;
    align-items: center;
    gap: 0.4vw;
  }

  @media screen and (orientation: portrait) {
    font-size: 1.8vw;
    height: 4.6vh;
  }
`

const generateTrClass = (rank: number) => {
  let className = ''
  switch (rank) {
  case 1:
    className += 'first'
    break
  case 2:
    className += 'second'
    break
  case 3:
    className += 'third'
    break
  }
  return className
}

const generateTdClass = (index: number) => {
  let className = ''

  switch (index) {
  case 0:
  case 3:
    className += 'number'
    break

  default:
    break
  }

  return className
}

export function RankTableRow({ tuple, nation = 'kr', cssProps }: RankTableRowProps) {
  const { prizes } = useFetchPrizes()

  const renderPrize = (data: number | string) => {
    return prizes.get(data) && <Prize content={prizes.get(data)} color={getPrizeColor(data as number)} />
  }

  return (
    <tr className={generateTrClass(tuple[0])} css={[trStyle, cssProps]}>
      {tuple.map((data, idx) => (
        <td
          className={generateTdClass(idx)}
          key={`data:${data}-${idx}`}
          css={tdStyle}
        >
          {
            idx === 1 && renderPrize(data)
          }
          {
            idx === 2 && (<NationFlag nation={nation}/>)
          }
          { idx !== 1 && data }
        </td>
      ))}
    </tr>
  )
}

interface PrizeColors {
  [key: number]: string
}

const prizeColors: PrizeColors = {
  1: 'rgba(255, 225, 65, 1)',
  2: 'rgba(248, 248, 248, 0.86)',
  3: '#eca65c',
} as const

function getPrizeColor(key: number) {
  return key in prizeColors ? prizeColors[key] : '#7CC4F8'
}

const Prize = memo(function Prize({
  content,
  color = '#CD5563',
}: {
  content: string
  color?: string
}) {
  return (
    <span
      css={css`
        display: flex;
        align-items: center;
        gap: 8px;
        color: ${color};
      `}
    >
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='1.2em'
        height='1.2em'
        viewBox='0 0 24 24'
        fill='none'
      >
        <path
          fillRule='evenodd'
          clipRule='evenodd'
          d='M2 4H22V10.586L20.5858 12.0002L22 13.4143V20H2V13.4143L2.00003 13.4144L3.41424 12.0002L2.00003 10.5859L2 10.586V4ZM11.8099 14.8406C11.8327 14.9321 11.9126 15 12.0069 15C12.102 15 12.1823 14.9308 12.2049 14.8384C12.276 14.5472 12.4145 14.2339 12.625 13.8984C13.0945 13.1575 14.0118 12.4304 14.8399 12.1956C14.9323 12.1694 15 12.0874 15 11.9913C15 11.896 14.9334 11.8144 14.8416 11.7889C14.4666 11.6848 14.0984 11.5035 13.7422 11.25C12.9704 10.7039 12.3886 9.9068 12.2055 9.16215C12.1827 9.06926 12.1018 9 12.0062 9C11.912 9 11.8319 9.06725 11.8087 9.15856C11.7179 9.51637 11.5499 9.86459 11.3047 10.2187C10.7828 10.9759 9.98427 11.5462 9.15637 11.7867C9.06527 11.8132 9 11.895 9 11.9899C9 12.0869 9.06814 12.1698 9.16156 12.1956C9.55458 12.3045 9.95219 12.5093 10.3438 12.8047C11.1797 13.4316 11.65 14.1981 11.8099 14.8406Z'
          fill='currentColor'
        />
      </svg>
      {content}
    </span>
  )
})
